// ComunicacionFolleteria — /admin/comunicacion/folleteria
// Tercer puesto de la agencia: material impreso (26-jul-2026).
// Junta bajo Comunicación lo que ya existía disperso:
//   - Folletos A5 nuevos (vecinos + empresas, dark glass, QR reales) diseñados hoy
//     → servidos desde /folletos/folletos-limpack-A5-final.html (copiado a public/).
//   - Builder de volantes para edificios (/admin/marketing/volante, volanteRenderer.js).
//   - Lotes & tracking QR (/admin/marketing/lotes): cada lote tiene slug
//     limpack.uy/r/{slug} → escaneos medidos en material_lote_visitas.
// KPIs objetivo (playbook): 500 folletos y 15 escaneos en 30d → 2.000 / 60 / 10 pedidos en 90d.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { AGENTES_COM, AgenteHero, COM_CSS } from '../../components/comunicacion/ComunicacionUI'

// PDFs en CMYK real para imprenta (ago-2026). Render Chromium (paper size explícito) → PDF/X-3
// con perfil FOGRA39 (coated) como OutputIntent, DeviceCMYK, sin objetos RGB. Sin emojis
// (íconos vectoriales SVG → convierten limpio a CMYK). Regenerar si se actualiza el HTML.
// Ver DECISIONES.md 2026-08-19.
// B2C — folleto nuevo LimpackFlex (vecinos): "Comprá hoy, ahorrá hoy y te llega hoy".
const B2C_HTML = '/folletos/folleto-b2c-limpackflex.html'
const B2C_PDF_CASA = '/folletos/folleto-b2c-limpackflex-casa.pdf'         // A5 148×210, CMYK PDF/X-3
const B2C_PDF_IMPRENTA = '/folletos/folleto-b2c-limpackflex-imprenta.pdf' // 154×216, sangría 3mm, TrimBox 148×210, CMYK PDF/X-3 FOGRA39
// B2B — folleto empresas (administradoras/comercios, QR a /cotizar).
// Mismo estilo que el B2C (flat navy, sin banding, Inter). Regenerado ago-2026.
const B2B_HTML = '/folletos/folleto-b2b-empresas.html'                   // preview standalone (mismo diseño que B2C)
const B2B_PDF_CASA = '/folletos/folleto-b2b-empresas-casa.pdf'           // A5 148×210, CMYK PDF/X-3
const B2B_PDF_IMPRENTA = '/folletos/folleto-b2b-empresas-imprenta.pdf'   // 154×216, sangría 3mm, TrimBox 148×210, CMYK PDF/X-3 FOGRA39

export default function ComunicacionFolleteria() {
  const [loading, setLoading] = useState(true)
  const [lotes, setLotes] = useState([])
  const [totales, setTotales] = useState({ impresos: 0, visitas: 0, lotes: 0 })
  const [verPreview, setVerPreview] = useState(false)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      // material_lotes_kpi: vista existente con visitas por lote (la usa /admin/marketing/lotes)
      const { data } = await supabase
        .from('material_lotes_kpi')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(12)
      const ls = data || []
      // Solo papel: los slugs 'digital' (atribución IG/email) no son folletería
      const papel = ls.filter(l => l.formato !== 'digital')
      setLotes(papel)
      setTotales({
        impresos: papel.reduce((s, l) => s + (Number(l.cantidad) || 0), 0),
        visitas: papel.reduce((s, l) => s + (Number(l.visitas_total ?? l.visitas ?? 0) || 0), 0),
        lotes: papel.length,
      })
    } catch (e) {
      console.error('Folleteria cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AdminLayout title="Folletería">
      <div className="com-wrap">
        <AgenteHero agente={AGENTES_COM.folleteria} ultimaCorrida={null} />

        {/* KPIs papel */}
        <div className="comf-kpis">
          <div className="comf-kpi">
            <div className="comf-kpi-num">{totales.impresos}</div>
            <div className="comf-kpi-lbl">folletos impresos (lotes) · meta 500 en 30d</div>
          </div>
          <div className="comf-kpi">
            <div className="comf-kpi-num">{totales.visitas}</div>
            <div className="comf-kpi-lbl">escaneos QR · meta 15 en 30d</div>
          </div>
          <div className="comf-kpi">
            <div className="comf-kpi-num">{totales.lotes}</div>
            <div className="comf-kpi-lbl">lotes de papel activos</div>
          </div>
        </div>

        {/* FOLLETO B2C — LimpackFlex (nuevo, ago-2026) */}
        <section className="com-card" style={{ borderLeft: '4px solid #00A8B5' }}>
          <div className="com-card-head">
            <div>
              <h2 className="com-card-title">🆕 Folleto B2C · LimpackFlex (vecinos)</h2>
              <span className="com-card-sub">
                “Comprá hoy, ahorrá hoy y te llega hoy”: LimpackFlex como diferencial + la tienda (+85 productos) + suscripción para ahorrar más. QR a la tienda.
                PDF en <strong>CMYK real</strong> (PDF/X-3, perfil FOGRA39, sin RGB). Imprenta: 154 × 216 mm con sangría 3 mm, corte a 148 × 210 (TrimBox).
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setVerPreview(v => !v)} className="com-btn com-btn-ghost">
                {verPreview ? 'Ocultar preview' : '👁 Ver preview'}
              </button>
              <a href={B2C_HTML} target="_blank" rel="noopener noreferrer" className="com-btn com-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                🖨 Abrir HTML ↗
              </a>
              <a href={B2C_PDF_CASA} download className="com-btn com-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                ⬇ PDF casa · A5 (CMYK)
              </a>
              <a href={B2C_PDF_IMPRENTA} download className="com-btn com-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                ⬇ PDF imprenta · 154×216 (CMYK)
              </a>
            </div>
          </div>
          {verPreview && (
            <iframe src={B2C_HTML} title="Folleto B2C LimpackFlex" className="comf-iframe" />
          )}
          <div className="com-card-sub" style={{ marginTop: 10 }}>
            Antes de repartir: creá un lote con su slug en <Link to="/admin/marketing/lotes">Lotes & tracking</Link> para
            que cada tanda tenga su QR propio y sepamos qué barrio escanea.
          </div>
        </section>

        {/* FOLLETO B2B — Empresas */}
        <section className="com-card" style={{ borderLeft: '4px solid #1A3461' }}>
          <div className="com-card-head">
            <div>
              <h2 className="com-card-title">🏢 Folleto B2B · Empresas</h2>
              <span className="com-card-sub">
                “Tu local o tus edificios, resueltos cada mes.” Para administradoras, comercios y oficinas · QR a la tienda (/empresas).
                Mismo diseño que el B2C. PDF en <strong>CMYK real</strong> (PDF/X-3, FOGRA39). Imprenta: 154 × 216 mm con sangría 3 mm, corte a 148 × 210.
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href={B2B_HTML} target="_blank" rel="noopener noreferrer" className="com-btn com-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                🖨 Abrir HTML ↗
              </a>
              <a href={B2B_PDF_CASA} download className="com-btn com-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                ⬇ PDF casa · A5 (CMYK)
              </a>
              <a href={B2B_PDF_IMPRENTA} download className="com-btn com-btn-primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                ⬇ PDF imprenta · 154×216 (CMYK)
              </a>
            </div>
          </div>
        </section>

        {/* HERRAMIENTAS EXISTENTES */}
        <div className="comf-tools">
          <Link to="/admin/marketing/volante" className="comf-tool">
            <span className="comf-tool-emoji">🧩</span>
            <div>
              <div className="comf-tool-title">Builder de volantes</div>
              <div className="comf-tool-desc">A4 / A5 / A6 / tarjeta portero por bloques (hero, descuento vecinos, pasos, QR). Descarga SVG lista para imprimir.</div>
            </div>
            <span className="comf-tool-go">→</span>
          </Link>
          <Link to="/admin/marketing/lotes" className="comf-tool">
            <span className="comf-tool-emoji">🔗</span>
            <div>
              <div className="comf-tool-title">Lotes & tracking QR</div>
              <div className="comf-tool-desc">Cada lote impreso tiene su slug limpack.uy/r/… — escaneos y visitas medidos por lote, edificio y barrio.</div>
            </div>
            <span className="comf-tool-go">→</span>
          </Link>
        </div>

        {/* LOTES RECIENTES */}
        <section className="com-card">
          <div className="com-card-head">
            <h2 className="com-card-title">📦 Lotes de papel recientes</h2>
            <Link to="/admin/marketing/lotes" className="com-card-sub">gestionar lotes →</Link>
          </div>
          {loading && <div className="com-empty">Cargando…</div>}
          {!loading && lotes.length === 0 && (
            <div className="com-empty">
              <strong>Sin lotes de papel todavía.</strong>
              Creá el primero en Lotes & tracking cuando imprimas los A5 nuevos.
            </div>
          )}
          {!loading && lotes.length > 0 && (
            <div className="comf-lotes">
              {lotes.map(l => (
                <div key={l.id || l.slug} className="comf-lote">
                  <span className="com-pill com-pill-gray">{l.formato}</span>
                  <span className="comf-lote-slug">/r/{l.slug}</span>
                  <span className="comf-lote-desc">{l.descripcion || l.destino || '—'}</span>
                  <span className="comf-lote-nums">
                    {Number(l.cantidad) || 0} impresos · {Number(l.visitas_total ?? l.visitas ?? 0) || 0} escaneos
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <style>{COM_CSS}</style>
      <style>{`
        .comf-kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        @media (max-width: 700px) { .comf-kpis { grid-template-columns: 1fr; } }
        .comf-kpi { background: #fff; border: 1px solid #E5E7EB; border-radius: 12px; padding: 12px; text-align: center; }
        .comf-kpi-num { font-size: 24px; font-weight: 900; color: #1A3461; }
        .comf-kpi-lbl { font-size: 10.5px; color: #6B7280; margin-top: 3px; }
        .comf-folletos-info { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; }
        .comf-folleto-chip {
          font-size: 12px; color: #374151; background: #F8F6F1; border: 1px solid #E9E5DA;
          border-radius: 999px; padding: 5px 12px;
        }
        .comf-iframe {
          width: 100%; height: 640px; border: 1px solid #E5E7EB; border-radius: 10px;
          margin-top: 10px; background: #3a4252;
        }
        .comf-tools { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        @media (max-width: 700px) { .comf-tools { grid-template-columns: 1fr; } }
        .comf-tool {
          display: flex; gap: 12px; align-items: center; padding: 14px 16px;
          background: #fff; border: 1px solid #E5E7EB; border-radius: 12px;
          text-decoration: none; color: inherit; transition: box-shadow .12s;
        }
        .comf-tool:hover { box-shadow: 0 4px 14px rgba(26,52,97,.1); }
        .comf-tool-emoji { font-size: 24px; }
        .comf-tool-title { font-size: 13.5px; font-weight: 800; color: #1A3461; }
        .comf-tool-desc { font-size: 11.5px; color: #6B7280; margin-top: 2px; line-height: 1.4; }
        .comf-tool-go { margin-left: auto; color: #00A8B5; font-weight: 800; }
        .comf-lotes { display: flex; flex-direction: column; gap: 6px; }
        .comf-lote {
          display: flex; align-items: center; gap: 10px; padding: 8px 10px;
          background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 12.5px; flex-wrap: wrap;
        }
        .comf-lote-slug { font-weight: 800; color: #00666B; font-family: ui-monospace, Menlo, monospace; font-size: 11.5px; }
        .comf-lote-desc { flex: 1; color: #374151; min-width: 140px; }
        .comf-lote-nums { color: #6B7280; font-size: 11.5px; }
      `}</style>
    </AdminLayout>
  )
}
